import React from 'react';
import { Trans } from '@lingui/macro';
import { TableWrap } from './FeatureInfoDrawer';

// Body content for the Metadata info drawer. Recognized keys vary by integration
// type, and the API stores metadata as a free-form hash with no per-key schema
// (it only validates the value is a hash) — the whole set is passed through to
// consuming apps, so some keys drive behavior in INF while others are read only
// by the UI or the consuming application. This catalog therefore lives UI-side.
const MetadataInfoContent = () => (
  <>
    <ul>
      <li>
        <Trans>
          Metadata is a set of custom key/value pairs stored on the integration.
          Metadata drives the integration experience: what data is shown,
          attributes consuming applications need to handle downstream logic, and
          how the integration behaves depends on the keys defined for that
          integration type.
        </Trans>
      </li>
      <li>
        <Trans>Recognized keys vary by integration type. Known examples:</Trans>
      </li>
    </ul>

    <h4>
      <Trans>Campaign integrations</Trans>
    </h4>
    <ul>
      <li>
        <code>origin</code>:{' '}
        <Trans>the source of the campaign (e.g., Isuzu)</Trans>
      </li>
    </ul>

    <h4>
      <Trans>Telematics integrations</Trans>
    </h4>
    <ul>
      <li>
        <code>origin</code>: <Trans>the telematics origin</Trans>
      </li>
      <li>
        <code>display_name</code>:{' '}
        <Trans>the name shown on the telematics tab</Trans>
      </li>
      <li>
        <code>hours_before_triggered_at</code>:{' '}
        <Trans>
          how far back telematics history is shown, based on the case’s created
          time and the date/time of the fault or diagnostic reading, so only
          recent telematics data appears on the case
        </Trans>
      </li>
    </ul>
    <p>
      <Trans>
        Other integration types may use additional keys not listed here.
      </Trans>
    </p>

    <h4>
      <Trans>
        What other known metadata keys are established for other integration
        types?
      </Trans>
    </h4>
    <p>
      <Trans>
        The backend has no central schema of valid keys; it only validates that
        metadata is a hash. The whole hash is echoed to the UI and consumers
        through passthrough serializers, so some keys “drive the experience”
        downstream even though the INF backend never reads them. Known keys,
        verified against the backend:
      </Trans>
    </p>
    <TableWrap>
      <table>
        <thead>
          <tr>
            <th>
              <Trans>Key</Trans>
            </th>
            <th>
              <Trans>Integration type</Trans>
            </th>
            <th>
              <Trans>Read by</Trans>
            </th>
            <th>
              <Trans>Notes</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>origin</code>
            </td>
            <td>
              <Trans>Broad: campaign, telematics, operations, messaging</Trans>
            </td>
            <td>
              <Trans>
                INF backend (many commands), stamped on every invocation
              </Trans>
            </td>
            <td>
              <Trans>Confirmed, widely used</Trans>
            </td>
          </tr>
          <tr>
            <td>
              <code>display_name</code>
            </td>
            <td>
              <Trans>Telematics</Trans>
            </td>
            <td>
              <Trans>UI/consumer only (serializer passthrough)</Trans>
            </td>
            <td>
              <Trans>Real key, but INF backend never reads it</Trans>
            </td>
          </tr>
          <tr>
            <td>
              <code>hours_before_triggered_at</code>
            </td>
            <td>
              <Trans>Telematics</Trans>
            </td>
            <td>
              <Trans>UI/consumer only</Trans>
            </td>
            <td>
              <Trans>
                Defined and consumed entirely by the INF UI/consuming app. Still
                a valid telematics key, just not INF-backend logic.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <code>recommended_operation_type</code>
            </td>
            <td>
              <Trans>Operations (carrier/recommended ops)</Trans>
            </td>
            <td>
              <Trans>INF backend</Trans>
            </td>
            <td>
              <Trans>
                Passed as <code>op_type</code> to the recommended-operations
                fetch
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <code>respond_over</code>
            </td>
            <td>
              <Trans>Generic (websocket-response integrations)</Trans>
            </td>
            <td>
              <Trans>INF backend</Trans>
            </td>
            <td>
              <Trans>
                Value <code>websocket</code> pushes results over WSS instead of
                HTTP
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <code>part_price_label</code>
            </td>
            <td>
              <Trans>Manufacturer parts pricing</Trans>
            </td>
            <td>
              <Trans>Pricing app</Trans>
            </td>
            <td>
              <Trans>
                OEM “Info column” label (e.g. “Tico”); auto-seeded on create
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <code>part_price_tooltip</code>
            </td>
            <td>
              <Trans>Manufacturer parts pricing</Trans>
            </td>
            <td>
              <Trans>Pricing app</Trans>
            </td>
            <td>
              <Trans>
                OEM “Info column” tooltip (e.g. “Tico Parts”); auto-seeded
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <code>part_price_type</code>
            </td>
            <td>
              <Trans>Manufacturer parts pricing</Trans>
            </td>
            <td>
              <Trans>Pricing app</Trans>
            </td>
            <td>
              <Trans>e.g. “warranty”; user-supplied, preserved on save</Trans>
            </td>
          </tr>
        </tbody>
      </table>
    </TableWrap>
  </>
);

export default MetadataInfoContent;
